/**
 * React hook wrapping EventSource for the Praxara SSE feed.
 *
 * Automatically re-subscribes when the access token rotates (e.g. after a
 * refresh-token exchange). Invalidates TanStack Query keys based on the event
 * type so the rest of the UI can stay fully declarative.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';

export interface PraxaraStreamEvent<T = unknown> {
  type: string;
  tenantId: string;
  resourceId?: string;
  ts: string;
  payload: T;
}

export function useEventStream() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!accessToken) {
      sourceRef.current?.close();
      sourceRef.current = null;
      return;
    }

    // Use VITE_API_URL or default to same origin
    const base = import.meta.env.VITE_API_URL ?? '';
    const url = `${base}/api/events/stream?token=${encodeURIComponent(accessToken)}`;
    const es = new EventSource(url);
    sourceRef.current = es;

    es.addEventListener('document.status', (e) => {
      const evt = parse(e);
      qc.invalidateQueries({ queryKey: ['documents'] });
      if (evt?.resourceId) qc.invalidateQueries({ queryKey: ['document', evt.resourceId] });
    });

    es.addEventListener('document.indexed', () => {
      qc.invalidateQueries({ queryKey: ['documents'] });
    });

    es.addEventListener('workflow.status', (e) => {
      const evt = parse<{ status: string; workflowName?: string }>(e);
      qc.invalidateQueries({ queryKey: ['runs'] });
      if (evt?.resourceId) qc.invalidateQueries({ queryKey: ['run', evt.resourceId] });
      if (evt?.payload?.status === 'AWAITING_REVIEW') {
        toast.info('Review required', evt.payload.workflowName ?? 'A workflow is awaiting your review.');
      }
    });

    es.addEventListener('extraction.created', () => {
      qc.invalidateQueries({ queryKey: ['extractions'] });
    });

    es.addEventListener('pv.case.updated', (e) => {
      const evt = parse(e);
      qc.invalidateQueries({ queryKey: ['pv', 'cases'] });
      if (evt?.resourceId) qc.invalidateQueries({ queryKey: ['pv', 'case', evt.resourceId] });
    });

    es.addEventListener('compliance.scan.updated', () => {
      qc.invalidateQueries({ queryKey: ['compliance', 'scans'] });
    });

    es.addEventListener('notification', (e) => {
      const evt = parse<{ title: string; body?: string; kind?: 'info' | 'success' | 'warning' | 'error' }>(e);
      qc.invalidateQueries({ queryKey: ['notifications'] });
      if (evt?.payload?.title) {
        const fn =
          evt.payload.kind === 'error'
            ? toast.error
            : evt.payload.kind === 'warning'
              ? toast.warning
              : evt.payload.kind === 'success'
                ? toast.success
                : toast.info;
        fn(evt.payload.title, evt.payload.body);
      }
    });

    es.onerror = () => {
      // EventSource auto-reconnects, but on a sustained failure we just close
      // and let the next render re-create. Avoid a tight reconnect loop in
      // broken-token scenarios.
    };

    return () => {
      es.close();
      sourceRef.current = null;
    };
  }, [accessToken, qc]);
}

function parse<T = unknown>(e: MessageEvent): PraxaraStreamEvent<T> | null {
  try {
    return JSON.parse(e.data as string) as PraxaraStreamEvent<T>;
  } catch {
    return null;
  }
}
